import React from 'react';
import { motion, Variants } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import aleft from '@/assets/about-left.jpg';
import aright from '@/assets/about-right.jpeg';
import '@/components/sections/Intro.scss';

interface WindowItem {
  to?: string;
  title?: string;
  description?: string;
  imageUrl: string;
  imageAlt: string;
  buttonText?: string;
  buttonIcon?: React.ReactNode;
}

const LEFT_PANEL_DATA: WindowItem = {
  description:
    'Ephemera Partners manages and deploys long-term capital across the experience economy, with a focus on real estate and aviation. Our strategy centres on the multi-trillion dollar secular shift in the human condition towards the global prioritization of meaningful experiences over material goods. Our thesis is that the infrastructure of exceptional experience is scarce while demand is structural. The affluent are spending more on experience, and more people are becoming affluent. We invest in that scarcity - a loft with a river view terrace in central Lisbon, a canalside apartment in historic Amsterdam, and structured exposure to the aviation assets that connect the world to both.',
  imageUrl: aleft,
  imageAlt: 'Modern apartment overlooking Amsterdam canal',
};

const RIGHT_PANEL_DATA: WindowItem = {
  description:
    "These assets share the same core characteristic: a finite claim on the physical world. Amsterdam's canal belt took five centuries and a civilisation's worth of engineering to create. The aircraft that transports you there represents the combined output of the most complex manufacturing ecosystem humanity has ever built, at production levels far below those the world wants. Demand for both compounds. The logic is deliberately integrated. We own assets at the destination - properties in the historic cores of cities that draw the world’s most mobile, experience-driven residents and visitors. We hold structured exposure to the aviation assets that carry people there. Both sit at the same chokepoint: irreplaceable physical infrastructure at the heart of a demand curve that only moves in one direction over the long term.",
  imageUrl: aright,
  imageAlt: 'Historic building facade in Amsterdam West',
};

const windowVariants: Variants = {
  hidden: (isLeft: boolean) => ({ x: isLeft ? '-100%' : '100%' }),
  visible: {
    x: 0,
    transition: { type: 'tween', ease: [0.25, 0.1, 0.25, 1], duration: 0.7 },
  },
};

const renderPanel = (item: WindowItem, isLeft: boolean) => {
  const isLink = !!item.to;

  const PanelWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) =>
    item.to ? (
      <Link to={item.to} className="intro__panel-link">{children}</Link>
    ) : (
      <div className="intro__panel-link">{children}</div>
    );

  return (
    <motion.div
      className={`intro__panel intro__panel--${isLeft ? 'left' : 'right'} ${isLink ? 'intro__panel--link' : 'intro__panel--static'}`}
      variants={windowVariants}
      custom={isLeft}
      whileHover={isLink ? { flexGrow: 1.2, zIndex: 10, transition: { duration: 0.3 } } : undefined}
    >
      <PanelWrapper>
        <div className="intro__panel-content">
          <header className="intro__panel-header">
            {item.title && <h2 className="intro__panel-title">{item.title}</h2>}
            {item.description && (
              <p className="intro__panel-description">{item.description}</p>
            )}
          </header>
          {item.to && (
            <motion.button
              className="intro__panel-btn"
              aria-label={item.buttonText || (item.title ? `Go to ${item.title}` : 'View')}
              disabled={!item.to}
              whileHover={{
                backgroundColor: '#FFFFFF',
                opacity: 0.5,
                color: '#000000',
                scale: 1.1,
                transition: { duration: 0.3 },
              }}
            >
              {item.buttonText && <span className="intro__panel-btn-text">{item.buttonText}</span>}
              {item.buttonIcon || <ChevronRight size={24} />}
            </motion.button>
          )}
        </div>
        <motion.img
          src={item.imageUrl}
          alt={item.imageAlt}
          className="intro__panel-image"
          transition={{ duration: 0.5 }}
          whileHover={isLink ? { opacity: 0.4, scale: 1.05, transition: { duration: 0.5 } } : undefined}
        />
      </PanelWrapper>
    </motion.div>
  );
};

const Intro: React.FC = () => (
  <section id="about" className="intro">
    <motion.div
      className="intro__wrapper"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
    >
      <div className="intro__split">
        <motion.div
          className="intro__split-track"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          {renderPanel(LEFT_PANEL_DATA, true)}
          {renderPanel(RIGHT_PANEL_DATA, false)}
        </motion.div>
      </div>
    </motion.div>
  </section>
);

export default Intro;
